import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { 
  Users, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Bell,
  DollarSign,
  Loader2,
  Edit2,
  Save,
  Eye,
  PauseCircle,
  ExternalLink,
  Ticket,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

const SubscriptionManager = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [config, setConfig] = useState(null);
  const [editConfigOpen, setEditConfigOpen] = useState(false);
  const [editConfig, setEditConfig] = useState({ payment_alias: '', payment_holder: '', price: '' });
  const [savingConfig, setSavingConfig] = useState(false);

  // Per-admin raffles summary (expanded view)
  const [expandedAdmin, setExpandedAdmin] = useState(null); // admin_id currently expanded
  const [summary, setSummary] = useState({}); // { [adminId]: {raffles:[], sorteos:[], loading:bool} }

  const toggleAdminSummary = async (adminId) => {
    if (expandedAdmin === adminId) {
      setExpandedAdmin(null);
      return;
    }
    setExpandedAdmin(adminId);
    if (summary[adminId]?.raffles) return; // already loaded
    setSummary(prev => ({ ...prev, [adminId]: { loading: true } }));
    try {
      const response = await axios.get(`${API}/admins/${adminId}/raffles-summary`);
      setSummary(prev => ({ ...prev, [adminId]: { ...response.data, loading: false } }));
    } catch (error) {
      toast.error('Error al cargar rifas del suscriptor');
      setSummary(prev => ({ ...prev, [adminId]: { loading: false, error: true } }));
    }
  };

  const handlePauseSubscription = async (adminId, adminName) => {
    if (!window.confirm(`¿Pausar la suscripción de ${adminName}?\n\n• Se marca como vencida y se deshabilita inmediatamente.\n• Le llega un email avisándole para que se contacte y renueve.\n• Sus rifas/sorteos públicos siguen funcionando hasta que vos los pauses (desde Editar).`)) return;

    setProcessingId(adminId);
    try {
      await axios.post(`${API}/admins/${adminId}/pause-subscription`);
      toast.success('Suscripción pausada y aviso enviado');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pausar suscripción');
    } finally {
      setProcessingId(null);
    }
  };

  const handlePauseAllRaffles = async (adminId, adminName) => {
    if (!window.confirm(`¿Pausar TODAS las rifas y sorteos públicos de ${adminName}?\n\nNo afecta su suscripción ni el panel — sólo deja de mostrarlas a sus compradores.`)) return;
    setProcessingId(adminId);
    try {
      const r = await axios.post(`${API}/admins/${adminId}/pause-all-raffles`);
      toast.success(`Pausadas: ${r.data.raffles_paused} rifas, ${r.data.sorteos_paused} sorteos`);
      // Refresh summary
      setSummary(prev => ({ ...prev, [adminId]: undefined }));
      if (expandedAdmin === adminId) toggleAdminSummary(adminId);
      fetchAdmins();
    } catch (error) {
      toast.error('Error al pausar rifas');
    } finally {
      setProcessingId(null);
    }
  };

  // Inline action chips that appear under each admin's name/email in every section
  const renderAdminActions = (admin) => {
    if (admin.is_owner) return null;
    const isExpanded = expandedAdmin === admin.id;
    const data = summary[admin.id];
    const totalCount = data ? (data.raffles?.length || 0) + (data.sorteos?.length || 0) : null;
    const canPause = admin.enabled !== false && admin.subscription_status !== 'expired';

    return (
      <div className="flex items-center gap-2 flex-wrap mt-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => toggleAdminSummary(admin.id)}
          className="h-7 text-xs"
          data-testid={`view-raffles-${admin.id}`}
        >
          <Eye size={12} className="mr-1" />
          {isExpanded
            ? 'Ocultar rifas'
            : totalCount !== null
              ? `Ver rifas (${totalCount})`
              : 'Ver rifas'}
        </Button>
        {canPause && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePauseSubscription(admin.id, admin.name)}
            disabled={processingId === admin.id}
            className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
            data-testid={`pause-${admin.id}`}
          >
            {processingId === admin.id ? (
              <Loader2 className="animate-spin" size={12} />
            ) : (
              <>
                <PauseCircle size={12} className="mr-1" />
                Pausar suscripción
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  // Expanded panel showing list of raffles/sorteos for the admin
  const renderAdminSummary = (admin) => {
    if (expandedAdmin !== admin.id) return null;
    const data = summary[admin.id];
    if (!data || data.loading) {
      return (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Cargando rifas...
        </div>
      );
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const raffles = data.raffles || [];
    const sorteos = data.sorteos || [];
    const slug = data.admin?.slug;

    if (!raffles.length && !sorteos.length) {
      return (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
          Este suscriptor no tiene rifas ni sorteos creados.
        </div>
      );
    }

    const activeCount = raffles.filter(r => r.is_active).length + sorteos.filter(s => s.is_active).length;

    return (
      <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-3 text-sm">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {slug && (
            <a
              href={`${origin}/rifas/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-purple-700 hover:underline text-xs"
            >
              <ExternalLink size={12} /> Página pública del suscriptor: /rifas/{slug}
            </a>
          )}
          {activeCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePauseAllRaffles(admin.id, admin.name)}
              disabled={processingId === admin.id}
              className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
              data-testid={`pause-all-${admin.id}`}
            >
              {processingId === admin.id ? (
                <Loader2 className="animate-spin" size={12} />
              ) : (
                <>
                  <PauseCircle size={12} className="mr-1" />
                  Pausar todas ({activeCount})
                </>
              )}
            </Button>
          )}
        </div>
        {raffles.length > 0 && (
          <div>
            <p className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <Ticket size={14} /> Rifas ({raffles.length})
            </p>
            <ul className="space-y-1 ml-4">
              {raffles.map(r => (
                <li key={r.id} className="flex items-center gap-2 flex-wrap">
                  <a
                    href={`${origin}/rifa/${r.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:underline truncate max-w-[280px]"
                    data-testid={`raffle-link-${r.id}`}
                  >
                    {r.title}
                  </a>
                  <Badge variant={r.is_active ? 'default' : 'secondary'} className={`text-xs ${r.is_active ? 'bg-green-600' : ''}`}>
                    {r.is_active ? 'Activa' : 'Pausada'}
                  </Badge>
                  <span className="text-xs text-gray-500">{r.sold}/{r.total_numbers} vendidos</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {sorteos.length > 0 && (
          <div>
            <p className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <Gift size={14} /> Sorteos ({sorteos.length})
            </p>
            <ul className="space-y-1 ml-4">
              {sorteos.map(s => (
                <li key={s.id} className="flex items-center gap-2 flex-wrap">
                  <a
                    href={`${origin}/sorteo/${s.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:underline truncate max-w-[280px]"
                    data-testid={`sorteo-link-${s.id}`}
                  >
                    {s.title}
                  </a>
                  <Badge variant={s.is_active ? 'default' : 'secondary'} className={`text-xs ${s.is_active ? 'bg-green-600' : ''}`}>
                    {s.is_active ? 'Activo' : 'Pausado'}
                  </Badge>
                  <span className="text-xs text-gray-500">{s.sold}/{s.total_numbers}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const openEditConfig = () => {
    setEditConfig({
      payment_alias: config?.payment_alias || '',
      payment_holder: config?.payment_holder || '',
      price: config?.price?.toString() || '',
    });
    setEditConfigOpen(true);
  };

  const handleSaveConfig = async () => {
    if (!editConfig.payment_alias.trim()) {
      toast.error('El alias no puede estar vacío');
      return;
    }
    const priceNum = parseFloat(editConfig.price);
    if (!priceNum || priceNum <= 0) {
      toast.error('Ingresá un precio válido');
      return;
    }
    setSavingConfig(true);
    try {
      const payload = {
        payment_alias: editConfig.payment_alias.trim(),
        payment_holder: editConfig.payment_holder.trim(),
        price: priceNum,
      };
      const response = await axios.put(`${API}/subscription/config`, payload);
      setConfig(response.data);
      toast.success('Configuración actualizada');
      setEditConfigOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/subscription/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admins/all`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Error al cargar administradores');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (adminId) => {
    setProcessingId(adminId);
    try {
      await axios.post(`${API}/admins/${adminId}/toggle-enabled`);
      toast.success('Estado actualizado');
      fetchAdmins();
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error('Error al actualizar estado');
    } finally {
      setProcessingId(null);
    }
  };

  const handleActivateSubscription = async (adminId, adminName) => {
    if (!window.confirm(`¿Confirmar activación de suscripción para ${adminName}? Esto habilitará 30 días de acceso.`)) {
      return;
    }
    
    setProcessingId(adminId);
    try {
      await axios.post(`${API}/admins/${adminId}/activate-subscription`);
      toast.success('Suscripción activada correctamente');
      fetchAdmins();
    } catch (error) {
      console.error('Error activating subscription:', error);
      toast.error('Error al activar suscripción');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSendNotifications = async () => {
    setSendingNotifications(true);
    try {
      const response = await axios.post(`${API}/subscription/check-and-notify`);
      const { count, notifications_sent } = response.data;
      
      if (count > 0) {
        toast.success(`Se enviaron ${count} notificaciones`);
      } else {
        toast.info('No hay notificaciones pendientes para enviar');
      }
      
      fetchAdmins();
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Error al enviar notificaciones');
    } finally {
      setSendingNotifications(false);
    }
  };

  const getStatusBadge = (admin) => {
    if (!admin.enabled) {
      return <Badge variant="outline" className="bg-gray-100">Deshabilitado</Badge>;
    }
    
    switch (admin.subscription_status) {
      case 'trial':
        return <Badge className="bg-blue-500">Prueba</Badge>;
      case 'active':
        return <Badge className="bg-green-500">Activo</Badge>;
      case 'expired':
        return <Badge variant="destructive">Vencido</Badge>;
      default:
        return <Badge variant="outline">Sin suscripción</Badge>;
    }
  };

  const getStatusIcon = (admin) => {
    if (!admin.enabled) {
      return <XCircle className="text-gray-400" size={20} />;
    }
    
    switch (admin.subscription_status) {
      case 'trial':
        return <Clock className="text-blue-500" size={20} />;
      case 'active':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'expired':
        return <AlertTriangle className="text-red-500" size={20} />;
      default:
        return <XCircle className="text-gray-400" size={20} />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Separar admins por estado
  const pendingAdmins = admins.filter(a => a.status === 'pending');
  const approvedAdmins = admins.filter(a => a.status === 'approved');
  const expiredAdmins = approvedAdmins.filter(a => a.subscription_status === 'expired');
  const expiringSoon = approvedAdmins.filter(a => 
    a.enabled && 
    a.days_remaining !== undefined && 
    a.days_remaining <= 5 &&
    a.subscription_status !== 'expired'
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <p>Cargando administradores...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold">Gestión de Suscripciones</h2>
          <p className="text-gray-600 text-sm sm:text-base">Administrá las suscripciones de los organizadores</p>
        </div>
        <Button
          onClick={handleSendNotifications}
          disabled={sendingNotifications}
          variant="outline"
          className="border-purple-300 text-purple-700 hover:bg-purple-50 w-full sm:w-auto"
        >
          {sendingNotifications ? (
            <Loader2 className="mr-2 animate-spin" size={18} />
          ) : (
            <Bell className="mr-2" size={18} />
          )}
          Enviar Avisos de Vencimiento
        </Button>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Activos</p>
                <p className="text-2xl font-bold text-green-600">
                  {approvedAdmins.filter(a => a.subscription_status === 'active' && a.enabled).length}
                </p>
              </div>
              <CheckCircle className="text-green-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En Prueba</p>
                <p className="text-2xl font-bold text-blue-600">
                  {approvedAdmins.filter(a => a.subscription_status === 'trial' && a.enabled).length}
                </p>
              </div>
              <Clock className="text-blue-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Vencidos</p>
                <p className="text-2xl font-bold text-red-600">
                  {approvedAdmins.filter(a => a.subscription_status === 'expired').length}
                </p>
              </div>
              <AlertTriangle className="text-red-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Por Vencer</p>
                <p className="text-2xl font-bold text-amber-600">
                  {expiringSoon.length}
                </p>
              </div>
              <Calendar className="text-amber-500" size={32} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuración */}
      {config && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-purple-600" />
                  <span><strong>Prueba:</strong> {config.trial_days} días</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-purple-600" />
                  <span><strong>Suscripción:</strong> {config.subscription_days} días</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-purple-600" />
                  <span><strong>Precio:</strong> ${config.price?.toLocaleString('es-AR')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span><strong>Alias:</strong> {config.payment_alias}</span>
                </div>
                {config.payment_holder && (
                  <div className="flex items-center gap-2">
                    <span><strong>A nombre de:</strong> {config.payment_holder}</span>
                  </div>
                )}
              </div>
              <Button
                onClick={openEditConfig}
                variant="outline"
                size="sm"
                className="bg-white hover:bg-purple-100"
                data-testid="edit-subscription-config"
              >
                <Edit2 size={14} className="mr-1.5" />
                Editar alias / precio
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={editConfigOpen} onOpenChange={setEditConfigOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Datos de cobro de suscripciones</DialogTitle>
            <DialogDescription>
              Este alias y precio se muestran cuando un suscriptor necesita contratar o renovar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="config-alias">Alias de pago</Label>
              <Input
                id="config-alias"
                value={editConfig.payment_alias}
                onChange={(e) => setEditConfig({ ...editConfig, payment_alias: e.target.value })}
                placeholder="Ej: mi.alias.mp"
                data-testid="config-alias-input"
              />
            </div>
            <div>
              <Label htmlFor="config-holder">A nombre de (opcional)</Label>
              <Input
                id="config-holder"
                value={editConfig.payment_holder}
                onChange={(e) => setEditConfig({ ...editConfig, payment_holder: e.target.value })}
                placeholder="Nombre del titular de la cuenta"
                data-testid="config-holder-input"
              />
            </div>
            <div>
              <Label htmlFor="config-price">Precio mensual</Label>
              <Input
                id="config-price"
                type="number"
                min="1"
                step="0.01"
                value={editConfig.price}
                onChange={(e) => setEditConfig({ ...editConfig, price: e.target.value })}
                placeholder="Ej: 20000"
                data-testid="config-price-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditConfigOpen(false)}
              disabled={savingConfig}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveConfig}
              disabled={savingConfig}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="config-save-btn"
            >
              <Save size={16} className="mr-2" />
              {savingConfig ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suscripciones vencidas - sección prominente para reactivar */}
      {expiredAdmins.length > 0 && (
        <Card className="border-red-300 bg-red-50" data-testid="expired-subscriptions-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              Suscripciones Vencidas ({expiredAdmins.length})
            </CardTitle>
            <CardDescription>
              Hacé click en &quot;Reactivar&quot; para volver a habilitar la suscripción por {config?.subscription_days || 30} días.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {expiredAdmins.map(admin => (
                <div key={admin.id} className="bg-white p-3 rounded-lg border border-red-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{admin.name}</p>
                      <p className="text-sm text-gray-500 truncate">{admin.email}</p>
                      <p className="text-xs text-red-600 mt-1">
                        Venció el {formatDate(admin.subscription_end_date)}
                      </p>
                      {renderAdminActions(admin)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <Badge variant="destructive" className="whitespace-nowrap">Vencido</Badge>
                      <Button
                        size="sm"
                        onClick={() => handleActivateSubscription(admin.id, admin.name)}
                        disabled={processingId === admin.id}
                        className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                        data-testid={`reactivate-${admin.id}`}
                      >
                        {processingId === admin.id ? (
                          <Loader2 className="animate-spin" size={16} />
                        ) : (
                          <>
                            <CheckCircle size={16} className="mr-1" />
                            Reactivar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  {renderAdminSummary(admin)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Por vencer pronto */}
      {expiringSoon.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={20} />
              Vencen Pronto ({expiringSoon.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {expiringSoon.map(admin => (
                <div key={admin.id} className="bg-white p-3 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{admin.name}</p>
                      <p className="text-sm text-gray-500 truncate">{admin.email}</p>
                      {renderAdminActions(admin)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <Badge variant="outline" className="border-amber-400 text-amber-700 whitespace-nowrap">
                        {admin.days_remaining} días restantes
                      </Badge>
                      <Button
                        size="sm"
                        onClick={() => handleActivateSubscription(admin.id, admin.name)}
                        disabled={processingId === admin.id}
                        className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                      >
                        {processingId === admin.id ? (
                          <Loader2 className="animate-spin" size={16} />
                        ) : (
                          'Renovar'
                        )}
                      </Button>
                    </div>
                  </div>
                  {renderAdminSummary(admin)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de todos los admins */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users size={20} />
            Todos los Administradores ({approvedAdmins.length})
          </CardTitle>
          <CardDescription>
            Gestiona el acceso de los organizadores de rifas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {approvedAdmins.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p>No hay administradores registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="min-w-[600px] sm:min-w-0 px-4 sm:px-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 text-sm">Admin</th>
                      <th className="text-left py-3 px-2 text-sm">Estado</th>
                      <th className="text-left py-3 px-2 text-sm hidden sm:table-cell">Vencimiento</th>
                      <th className="text-left py-3 px-2 text-sm">Días</th>
                      <th className="text-center py-3 px-2 text-sm">Habilitado</th>
                      <th className="text-center py-3 px-2 text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedAdmins.map(admin => (
                      <React.Fragment key={admin.id}>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="flex items-start gap-2">
                              {getStatusIcon(admin)}
                              <div className="min-w-0">
                                <p className="font-semibold text-sm truncate max-w-[120px] sm:max-w-none">{admin.name}</p>
                                <p className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-none">{admin.email}</p>
                                {renderAdminActions(admin)}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {getStatusBadge(admin)}
                          </td>
                          <td className="py-3 px-2 hidden sm:table-cell text-sm">
                            {formatDate(admin.subscription_end_date)}
                          </td>
                          <td className="py-3 px-2">
                            {admin.subscription_end_date ? (
                              <span className={`text-sm font-medium ${
                                admin.days_remaining <= 5 
                                  ? 'text-red-600 font-bold' 
                                  : admin.days_remaining <= 10 
                                    ? 'text-amber-600' 
                                    : 'text-green-600'
                              }`}>
                                {admin.days_remaining}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Switch
                              checked={admin.enabled}
                              onCheckedChange={() => handleToggleEnabled(admin.id)}
                              disabled={processingId === admin.id}
                            />
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleActivateSubscription(admin.id, admin.name)}
                              disabled={processingId === admin.id}
                              className="border-green-300 text-green-700 hover:bg-green-50 text-xs px-2"
                            >
                              {processingId === admin.id ? (
                                <Loader2 className="animate-spin" size={14} />
                              ) : (
                                <>
                                  <DollarSign size={14} className="mr-1" />
                                  <span className="hidden sm:inline">Activar</span>
                                  <span className="sm:hidden">$</span>
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                        {expandedAdmin === admin.id && (
                          <tr>
                            <td colSpan={6} className="px-2 pb-3">
                              {renderAdminSummary(admin)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admins pendientes */}
      {pendingAdmins.length > 0 && (
        <Card className="border-blue-300">
          <CardHeader>
            <CardTitle className="text-blue-700">
              Solicitudes Pendientes ({pendingAdmins.length})
            </CardTitle>
            <CardDescription>
              Estos admins esperan aprobación. Aprobálos desde &quot;Pendientes&quot; en el menú.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingAdmins.map(admin => (
                <div key={admin.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-blue-50 p-3 rounded-lg gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{admin.name}</p>
                    <p className="text-sm text-gray-500 truncate">{admin.email}</p>
                  </div>
                  <Badge variant="outline" className="border-blue-400 self-start sm:self-center">Pendiente</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionManager;
